/**
 * Builds CodeMirror completion entries from MACRO_CATALOG and workflow variables.
 *
 * @module node-config/json-editor/macroCompletion
 */

import { MACRO_CATALOG, slugifyStepLabel } from '@chatsdet/types';
import type { CompletionEntry } from '@chatsdet/ui';
import type { WorkflowVariable } from '../../../pages/FlowDesigner/hooks/useWorkflowVariables';

export function buildCompletionEntries(variables: WorkflowVariable[]): CompletionEntry[] {
  const entries: CompletionEntry[] = [];

  // Macro entries
  for (const macro of MACRO_CATALOG) {
    const hasArgs = macro.args.length > 0;
    const label = hasArgs
      ? `$${macro.name}(${macro.args.map((a) => (a.optional ? `${a.name}?` : a.name)).join(', ')})`
      : `$${macro.name}()`;
    const apply = hasArgs
      ? `$${macro.name}()`
      : `$${macro.name}()`;

    entries.push({
      label,
      detail: macro.description,
      type: 'function',
      apply,
    });
  }

  // Variable entries
  for (const v of variables) {
    if (v.hasCollision) {
      for (const definer of v.allDefiners) {
        const qualifier = slugifyStepLabel(definer.stepIndex, definer.stepLabel);
        entries.push({
          label: `$var.${qualifier}.${v.name}`,
          detail: `From Step ${definer.stepIndex}: ${definer.stepLabel}`,
          type: 'variable',
        });
      }
    } else {
      entries.push({
        label: `$var.${v.name}`,
        detail: v.hasDesignTimeValue
          ? `Variable from ${v.source}`
          : 'Populated at runtime',
        type: 'variable',
      });
    }
  }

  return entries;
}
